"use client";

/**
 * DashboardStatsDisplay - Dashboard statistics and metrics
 */

import React, { useState } from "react";
import {
  Dashboard,
  TrendingUp,
  TrendingDown,
  Work,
  Email,
  Visibility,
  BarChartOutlined,
} from "@mui/icons-material";
import { Tag } from "@/ui";
import { colors, spacing, radius, textStyles, typography, primitives } from "@/ui/tokens";
import {
  ToolCard,
  ToolHeader,
  ToolBody,
  ToolProgress,
  ToolError,
  ToolEmptyState,
} from "../../Tool";
import { deriveToolState } from "@/lib/sola/types/tools";
import type { ToolDisplayProps } from "@/lib/sola/types/tools";

interface PeriodSummary {
  floatsCreated: number;
  emailsSent: number;
  floatsViewed: number;
}

interface StatsOutput {
  status?: string;
  stats?: {
    days_7?: {
      total?: { created?: number; sent?: number; viewed?: number };
      time_series?: unknown;
    };
    days_30?: {
      total?: { created?: number; sent?: number; viewed?: number };
      time_series?: unknown;
    };
  };
  summary?: {
    last7Days?: PeriodSummary;
    last30Days?: PeriodSummary;
  };
  message?: string;
  error?: string;
}

interface StatCardData {
  label: string;
  value: number;
  icon: React.ReactNode;
  bgColor: string;
  iconColor: string;
  change?: number;
}

function StatCard({ stat }: { stat: StatCardData }) {
  return (
    <div
      style={{
        padding: spacing[5],
        border: `1px solid ${colors.border.subtle}`,
        borderRadius: radius.md,
        height: "100%",
        backgroundColor: colors.canvas.default,
      }}
    >
      <div style={{ display: "flex", alignItems: "flex-start", justifyContent: "space-between" }}>
        <div>
          <span
            style={{
              ...textStyles.label,
              color: colors.text.muted,
              display: "block",
            }}
          >
            {stat.label}
          </span>
          <span
            style={{
              ...textStyles.h2,
              fontWeight: typography.fontWeight.medium,
              display: "block",
              marginTop: spacing[1],
            }}
          >
            {stat.value.toLocaleString()}
          </span>
          {stat.change !== undefined && stat.change !== 0 && (
            <div style={{ display: "flex", alignItems: "center", gap: spacing[1], marginTop: spacing[2] }}>
              {stat.change >= 0 ? (
                <TrendingUp style={{ fontSize: 16, color: colors.signal.success }} />
              ) : (
                <TrendingDown style={{ fontSize: 16, color: colors.signal.danger }} />
              )}
              <span
                style={{
                  ...textStyles.caption,
                  color: stat.change >= 0 ? colors.signal.success : colors.signal.danger,
                  fontWeight: typography.fontWeight.medium,
                }}
              >
                {stat.change >= 0 ? "+" : ""}{stat.change}%
              </span>
            </div>
          )}
        </div>
        <div
          style={{
            padding: spacing[2],
            borderRadius: radius.md,
            backgroundColor: stat.bgColor,
            color: stat.iconColor,
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
          }}
        >
          {stat.icon}
        </div>
      </div>
    </div>
  );
}

function buildStats(summary: PeriodSummary): StatCardData[] {
  return [
    {
      label: "Floats Created",
      value: summary.floatsCreated,
      icon: <Work />,
      bgColor: colors.action.productLight,
      iconColor: colors.action.product,
    },
    {
      label: "Emails Sent",
      value: summary.emailsSent,
      icon: <Email />,
      bgColor: primitives.pink[50],
      iconColor: colors.action.accent,
    },
    {
      label: "Floats Viewed",
      value: summary.floatsViewed,
      icon: <Visibility />,
      bgColor: colors.signal.successLight,
      iconColor: colors.signal.success,
    },
  ];
}

export function DashboardStatsDisplay({ toolCall, isStreaming }: ToolDisplayProps) {
  const [isExpanded, setIsExpanded] = useState(true);
  const [period, setPeriod] = useState<"7" | "30">("7");
  const toolState = deriveToolState(toolCall);
  const { status, isLoading, isError, error } = toolState;

  const result = toolCall.finalOutput as StatsOutput | undefined;

  const summary = period === "7"
    ? result?.summary?.last7Days
    : result?.summary?.last30Days;

  const stats = summary ? buildStats(summary) : [];

  return (
    <ToolCard status={status} noPadding>
      <ToolHeader
        title="Dashboard Statistics"
        toolName={toolCall.toolName}
        status={status}
        isExpanded={isExpanded}
        onToggleExpand={() => setIsExpanded((prev) => !prev)}
        icon={<Dashboard style={{ fontSize: 18 }} />}
      />

      <ToolBody isOpen={isExpanded} noPadding>
        {isLoading && (
          <div style={{ padding: spacing[5], textAlign: "center" }}>
            <ToolProgress variant="dots" label="Loading statistics..." />
          </div>
        )}

        {isError && error && (
          <div style={{ padding: spacing[4] }}>
            <ToolError error={error} />
          </div>
        )}

        {!isLoading && stats.length > 0 && (
          <div>
            <div
              style={{
                paddingLeft: spacing[5],
                paddingRight: spacing[5],
                paddingTop: spacing[3],
                paddingBottom: spacing[3],
                backgroundColor: colors.canvas.card,
                borderBottom: `1px solid ${colors.border.subtle}`,
                display: "flex",
                alignItems: "center",
                justifyContent: "space-between",
              }}
            >
              <span style={{ ...textStyles.body2, fontWeight: typography.fontWeight.medium }}>
                {period === "7" ? "Last 7 days" : "Last 30 days"}
              </span>
              <div style={{ display: "flex", gap: 0 }}>
                {(["7", "30"] as const).map((val) => (
                  <button
                    key={val}
                    onClick={() => setPeriod(val)}
                    style={{
                      padding: `${spacing[1]}px ${spacing[3]}px`,
                      border: `1px solid ${colors.border.subtle}`,
                      borderRadius: val === "7" ? `${radius.sm}px 0 0 ${radius.sm}px` : `0 ${radius.sm}px ${radius.sm}px 0`,
                      marginLeft: val === "30" ? -1 : 0,
                      backgroundColor: period === val ? colors.action.product : colors.canvas.default,
                      color: period === val ? colors.text.inverse : colors.text.primary,
                      cursor: "pointer",
                      ...textStyles.caption,
                      fontWeight: typography.fontWeight.medium,
                      transition: "all 0.15s ease",
                    }}
                  >
                    {val}d
                  </button>
                ))}
              </div>
            </div>

            <div
              style={{
                padding: spacing[5],
                display: "grid",
                gridTemplateColumns: "repeat(auto-fit, minmax(180px, 1fr))",
                gap: spacing[4],
              }}
            >
              {stats.map((stat) => (
                <StatCard stat={stat} key={stat.label} />
              ))}
            </div>
          </div>
        )}

        {!isLoading && stats.length === 0 && !isError && (
          <ToolEmptyState icon={<BarChartOutlined />} message={result?.message || "No statistics available"} />
        )}
      </ToolBody>
    </ToolCard>
  );
}

export default DashboardStatsDisplay;
